import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { EnableC3C5WorkflowStepsEnum } from 'src/app/settings/interfaces/EnableC3C5WorkflowStepsEnum';
import { IDrugOfInterest } from 'src/app/settings/interfaces/IDrugOfInterest';
import { DrugActivitiesToTrackEnum } from 'src/app/shared/interfaces/DrugActivitiesToTrackEnum';
import { IC3C5SettingValue } from 'src/app/shared/interfaces/IC3C5SettingValue.interface';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { SettingsState } from 'src/app/shared/states/settings/settings.state';
@UntilDestroy()
@Component({
  selector: 'app-enable-c3-c5-workflow',
  templateUrl: './enable-c3-c5-workflow.component.html',
  styleUrls: ['./enable-c3-c5-workflow.component.scss'],
})
export class EnableC3C5WorkflowComponent implements OnInit {

  private _updateDrugsOfInterest: boolean;
  @Input() 
  set updateDrugsOfInterest(value: boolean) {
    if (!!value) {
      this.step =  EnableC3C5WorkflowStepsEnum.CustomizeNdcList;
    }
    this._updateDrugsOfInterest = value;
  }

  @Output() cancel: EventEmitter<void> = new EventEmitter();
  @Output() save: EventEmitter<IC3C5SettingValue> = new EventEmitter();

  step: EnableC3C5WorkflowStepsEnum = EnableC3C5WorkflowStepsEnum.AdditionalChargeDialog;

  EnableC3C5WorkflowStepsEnum = EnableC3C5WorkflowStepsEnum;

  @Select(SettingsState.loading) loading$: Observable<boolean>;
  @Select(SettingsState.drugsOfInterest) drugsOfInterest$: Observable<IDrugOfInterest[]>;

  activityToTrackList = [
    {
      id: DrugActivitiesToTrackEnum.AllAndDrugsOfInterest,
      name: 'Track all controlled substances and drugs of interest',
      description:
        'Track schedule III, IV, V, gabapentin, and pseudoephedrine drug activities',
    },
    {
      id: DrugActivitiesToTrackEnum.CABoard,
      name: 'Track CA-Board requirements only - drug NDCs that match the following descriptions and strengths',
      descriptionList: [],
      settingValue: [],
    },
  ];

  customDrugsList = [];

  selectedDrugsOfInterestIds: string[];

  form: UntypedFormGroup;

  constructor(private fb: UntypedFormBuilder, private store: Store) {
    this.form = fb.group({
      activity: 0,
    });
  }

  ngOnInit(): void {
    this.drugsOfInterest$
    .pipe(untilDestroyed(this))
    .subscribe((drugsOfInterest) => {
      if (drugsOfInterest.length) {
        //init option 1
        this.customDrugsList = drugsOfInterest
        .filter(x => x.type === DrugActivitiesToTrackEnum.AllAndDrugsOfInterest)
        .map(x => ({id: x.id, name: x.name, value: (<any>x.value).flat()}));
        if (this._updateDrugsOfInterest) {
          this.selectedDrugsOfInterestIds = this.getIdsOfPreviouslySelectedValues();
        } else {
          //set all selected by default
          this.selectedDrugsOfInterestIds = this.customDrugsList.map(x => x.id);
        }
        //init CA Board List
       const caBoardDrugs = drugsOfInterest
        .filter(x => x.type === DrugActivitiesToTrackEnum.CABoard)
        .map(x => ({id: x.id, name: x.name, value: (<any>x.value).flat()}));

        const CABoardList = this.activityToTrackList.filter(x => x.id === DrugActivitiesToTrackEnum.CABoard)[0];
        CABoardList.descriptionList = caBoardDrugs.map(x => x.name);
        CABoardList.settingValue = caBoardDrugs.map(x => x.id);
      }
    });
  }

  saveSelected(): void {
    const id = this.form.get('activity').value;
    if (id === DrugActivitiesToTrackEnum.AllAndDrugsOfInterest) {
      this.step = EnableC3C5WorkflowStepsEnum.CustomizeNdcList;
    } else {
      this.save.next({
        DrugsOfInterestType: id,
        DrugsOfInterestValue: this.activityToTrackList.find((x) => x.id == id)
          ?.settingValue,
      });
    }
  }

  confirmCustomDrugsList() {
    this.save.next({
      DrugsOfInterestType: 0,
      DrugsOfInterestValue: (this.selectedDrugsOfInterestIds as any)?.flat(),
    });
  }

  drugOptionsChanged(option: string): void {
    const index = this.selectedDrugsOfInterestIds.indexOf(option);
    if (index > -1) {
      this.selectedDrugsOfInterestIds.splice(index, 1);
    } else {
      this.selectedDrugsOfInterestIds.push(option);
    }
  }

  isSelected(clickedItem: string): boolean {
    return this.selectedDrugsOfInterestIds?.indexOf(clickedItem) > -1;
  }

  cancelPickingDrugsOfInterest(): void {
    if (this._updateDrugsOfInterest) {
      this.cancel.next();
    }
    else this.step = EnableC3C5WorkflowStepsEnum.SelectDrugActivity;
  }

  private getIdsOfPreviouslySelectedValues(): string[] {
    //selected value from pharmacy preference
    const prevSelectedValues = this.store.selectSnapshot(PharmacyState.enableC3toC5DrugsValue).DrugsOfInterestValue;
    return prevSelectedValues ?
    this.customDrugsList.filter(x => x.value.some(val => prevSelectedValues.includes(val)))?.map(x => x.id) : [];
  }
}
